import React from 'react';
import { TabsContent } from '@/components/ui/tabs';
import {
  DetailsTab,
  TimeTrackingTab,
  ApprovalTab,
  ReportsTab,
  ManagementTab,
  DataIntegrationTab,
  AdministrationTab,
  UsersTab,
} from './tabs';
import type { DetailsTabProps } from './tabs/DetailsTab';
import type { SmartFeatureItem } from './tabs/TimeTrackingSmartFeaturesSection';
import type { AdminSubSettingItem } from './tabs/AdministrationSubSettings';
import type { BulkActions, PermissionState } from './hooks';
import type { UserData } from './mockRoleData';

type SmartFeatureItemWithSetter = SmartFeatureItem & { setter: (value: boolean) => void };

export interface EditRoleTabsContentProps {
  permissions: PermissionState;
  updatePermission: <K extends keyof PermissionState>(key: K, value: PermissionState[K]) => void;
  applyPermissions: (next: Partial<PermissionState>) => void;
  bulkActions: BulkActions;
  smartFeatureItems: SmartFeatureItemWithSetter[];
  adminSubSettings: AdminSubSettingItem[];
  isAdministrator: boolean;
  detailsProps: DetailsTabProps;
  users: UserData[];
  roleName: string;
  onTransferUser: (user: UserData) => void;
}

export const EditRoleTabsContent: React.FC<EditRoleTabsContentProps> = ({
  permissions,
  updatePermission,
  applyPermissions,
  bulkActions,
  smartFeatureItems,
  adminSubSettings,
  isAdministrator,
  detailsProps,
  users,
  roleName,
  onTransferUser,
}) => (
  <>
    <TabsContent value="details" className="space-y-4">
      <DetailsTab {...detailsProps} />
    </TabsContent>

    <TabsContent value="users" className="space-y-6">
      <UsersTab users={users} roleName={roleName} onTransferUser={onTransferUser} />
    </TabsContent>

    <TabsContent value="time-tracking" className="space-y-4">
      <TimeTrackingTab
        enabledPermissions={permissions.enabledPermissions}
        onTogglePermission={(permissionId, checked) => {
          const newPerms = new Set(permissions.enabledPermissions);
          if (checked) newPerms.add(permissionId); else newPerms.delete(permissionId);
          updatePermission('enabledPermissions', newPerms);
        }}
        viewTimesheetsAccess={permissions.viewTimesheetsAccess}
        onViewTimesheetsAccessChange={(v) => updatePermission('viewTimesheetsAccess', v)}
        editTimesheetsAccess={permissions.editTimesheetsAccess}
        onEditTimesheetsAccessChange={(v) => updatePermission('editTimesheetsAccess', v)}
        allowDeleteTimesheets={permissions.allowDeleteTimesheets}
        onAllowDeleteTimesheetsChange={(v) => updatePermission('allowDeleteTimesheets', v)}
        allowDuplicateTimesheets={permissions.allowDuplicateTimesheets}
        onAllowDuplicateTimesheetsChange={(v) => updatePermission('allowDuplicateTimesheets', v)}
        allowRecallTimesheet={permissions.allowRecallTimesheet}
        onAllowRecallTimesheetChange={(v) => updatePermission('allowRecallTimesheet', v)}
        smartFeatureItems={smartFeatureItems}
        onSmartFeatureChange={(item: SmartFeatureItem, value: boolean) => {
          smartFeatureItems.find(i => i.id === item.id)?.setter(value);
        }}
        onSelectAll={bulkActions.handleSelectAllTimeTracking}
        onDeselectAll={bulkActions.handleDeselectAllTimeTracking}
      />
    </TabsContent>

    <TabsContent value="approval" className="space-y-4">
      <ApprovalTab
        viewApprovalDashboard={permissions.viewApprovalDashboard}
        onViewApprovalDashboardChange={(v) => updatePermission('viewApprovalDashboard', v)}
        viewApprovals={permissions.viewApprovals}
        onViewApprovalsChange={(v) => updatePermission('viewApprovals', v)}
        editTimesheetsApproval={permissions.editTimesheetsApproval}
        onEditTimesheetsApprovalChange={(v) => updatePermission('editTimesheetsApproval', v)}
        rejectTimesheets={permissions.rejectTimesheets}
        onRejectTimesheetsChange={(v) => applyPermissions(v
          ? { rejectTimesheets: true }
          : { rejectTimesheets: false, compulsoryRejectionReason: false })}
        compulsoryRejectionReason={permissions.compulsoryRejectionReason}
        onCompulsoryRejectionReasonChange={(v) => updatePermission('compulsoryRejectionReason', v)}
        bulkApprove={permissions.bulkApprove}
        onBulkApproveChange={(v) => updatePermission('bulkApprove', v)}
        delegateApproval={permissions.delegateApproval}
        onDelegateApprovalChange={(v) => updatePermission('delegateApproval', v)}
        viewPolicyViolations={permissions.viewPolicyViolations}
        onViewPolicyViolationsChange={(v) => updatePermission('viewPolicyViolations', v)}
        viewDetectedIssues={permissions.viewDetectedIssues}
        onViewDetectedIssuesChange={(v) => updatePermission('viewDetectedIssues', v)}
        onSelectAll={bulkActions.handleSelectAllApproval}
        onDeselectAll={bulkActions.handleDeselectAllApproval}
      />
    </TabsContent>

    <TabsContent value="reports" className="space-y-4">
      <ReportsTab
        enableTimesheetReport={permissions.enableTimesheetReport}
        onEnableTimesheetReportChange={(v) => applyPermissions(v
          ? { enableTimesheetReport: true }
          : { enableTimesheetReport: false, allowTimesheetReportExport: false })}
        viewTimesheetReportAccess={permissions.viewTimesheetReportAccess}
        onViewTimesheetReportAccessChange={(v) => updatePermission('viewTimesheetReportAccess', v)}
        allowTimesheetReportExport={permissions.allowTimesheetReportExport}
        onAllowTimesheetReportExportChange={(v) => updatePermission('allowTimesheetReportExport', v)}
        enableMissingTimesheetReport={permissions.enableMissingTimesheetReport}
        onEnableMissingTimesheetReportChange={(v) => applyPermissions(v
          ? { enableMissingTimesheetReport: true }
          : { enableMissingTimesheetReport: false, allowMissingTimesheetReportExport: false })}
        viewMissingTimesheetReportAccess={permissions.viewMissingTimesheetReportAccess}
        onViewMissingTimesheetReportAccessChange={(v) => updatePermission('viewMissingTimesheetReportAccess', v)}
        allowMissingTimesheetReportExport={permissions.allowMissingTimesheetReportExport}
        onAllowMissingTimesheetReportExportChange={(v) => updatePermission('allowMissingTimesheetReportExport', v)}
        enableUtilisationReport={permissions.enableUtilisationReport}
        onEnableUtilisationReportChange={(v) => applyPermissions(v
          ? { enableUtilisationReport: true }
          : { enableUtilisationReport: false, allowUtilisationReportExport: false })}
        viewUtilisationReportAccess={permissions.viewUtilisationReportAccess}
        onViewUtilisationReportAccessChange={(v) => updatePermission('viewUtilisationReportAccess', v)}
        allowUtilisationReportExport={permissions.allowUtilisationReportExport}
        onAllowUtilisationReportExportChange={(v) => updatePermission('allowUtilisationReportExport', v)}
        enableAuditLog={permissions.enableAuditLog}
        onEnableAuditLogChange={(v) => applyPermissions(v
          ? { enableAuditLog: true }
          : { enableAuditLog: false, allowAuditLogExport: false })}
        viewAuditLogAccess={permissions.viewAuditLogAccess}
        onViewAuditLogAccessChange={(v) => updatePermission('viewAuditLogAccess', v)}
        allowAuditLogExport={permissions.allowAuditLogExport}
        onAllowAuditLogExportChange={(v) => updatePermission('allowAuditLogExport', v)}
        enablePostingLog={permissions.enablePostingLog}
        onEnablePostingLogChange={(v) => applyPermissions(v
          ? { enablePostingLog: true }
          : { enablePostingLog: false, allowPostingLogExport: false, allowPostingLogRetry: false })}
        viewPostingLogAccess={permissions.viewPostingLogAccess}
        onViewPostingLogAccessChange={(v) => updatePermission('viewPostingLogAccess', v)}
        allowPostingLogExport={permissions.allowPostingLogExport}
        onAllowPostingLogExportChange={(v) => updatePermission('allowPostingLogExport', v)}
        allowPostingLogRetry={permissions.allowPostingLogRetry}
        onAllowPostingLogRetryChange={(v) => updatePermission('allowPostingLogRetry', v)}
        onSelectAll={bulkActions.handleSelectAllReports}
        onDeselectAll={bulkActions.handleDeselectAllReports}
      />
    </TabsContent>

    <TabsContent value="management" className="space-y-4">
      <ManagementTab
        viewEmployeeDirectory={permissions.viewEmployeeDirectory}
        onViewEmployeeDirectoryChange={(v) => updatePermission('viewEmployeeDirectory', v)}
        viewEmployeeDirectoryAccess={permissions.viewEmployeeDirectoryAccess}
        onViewEmployeeDirectoryAccessChange={(v) => updatePermission('viewEmployeeDirectoryAccess', v)}
        viewEmployeeDetails={permissions.viewEmployeeDetails}
        onViewEmployeeDetailsChange={(v) => updatePermission('viewEmployeeDetails', v)}
        onSelectAll={bulkActions.handleSelectAllManagement}
        onDeselectAll={bulkActions.handleDeselectAllManagement}
      />
    </TabsContent>

    <TabsContent value="data" className="space-y-4">
      <DataIntegrationTab
        viewIntegrations={permissions.viewIntegrations}
        onViewIntegrationsChange={(v) => updatePermission('viewIntegrations', v)}
        manageIntegrations={permissions.manageIntegrations}
        onManageIntegrationsChange={(v) => updatePermission('manageIntegrations', v)}
        viewCompanies={permissions.viewCompanies}
        onViewCompaniesChange={(v) => updatePermission('viewCompanies', v)}
        manageCompanies={permissions.manageCompanies}
        onManageCompaniesChange={(v) => updatePermission('manageCompanies', v)}
        onSelectAll={bulkActions.handleSelectAllDataIntegration}
        onDeselectAll={bulkActions.handleDeselectAllDataIntegration}
      />
    </TabsContent>

    <TabsContent value="administration" className="space-y-4">
      <AdministrationTab
        manageGeneralSettings={permissions.manageGeneralSettings}
        onManageGeneralSettingsChange={(value) => applyPermissions(value === 'view'
          ? {
            manageGeneralSettings: 'view',
            manageDeadlines: false,
            managePolicySettings: false,
            manageUtilisationSettings: false,
            manageTimeEntrySettings: false,
            manageWorkTypeSettings: false,
            manageApprovalsSettings: false,
            manageNotifications: false,
            manageRolesPermissions: false,
            manageScreenDataModes: false,
          }
          : { manageGeneralSettings: value })}
        subSettings={adminSubSettings}
        onSubSettingChange={(item: AdminSubSettingItem, value: boolean) => {
          adminSubSettings.find(i => i.id === item.id)?.setter(value);
        }}
        manageRolesPermissions={permissions.manageRolesPermissions}
        onManageRolesPermissionsChange={(v) => updatePermission('manageRolesPermissions', v)}
        manageRates={permissions.manageRates}
        onManageRatesChange={(v) => updatePermission('manageRates', v)}
        isAdministrator={isAdministrator}
        onSelectAll={bulkActions.handleSelectAllAdministration}
        onDeselectAll={bulkActions.handleDeselectAllAdministration}
      />
    </TabsContent>
  </>
);
